import React, { Fragment, FunctionComponent, useMemo } from 'react';
import { Typography, Grid } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { ThreadSafetyTag, ApiTag, SecurityTag, EngineTag } from '@modules/ui/tags/types';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import CodeSamples from '@modules/ui/shared/components/CodeSamples';
import AlertMarkdown from '@modules/ui/shared/components/AlertMarkdown';
import { getMemberSlug } from '@modules/apiReference/page/utils/apiReferenceUtils';
import CodeSample from '@modules/utilities/interfaces/CodeSample';
import { isCallback, isMethod, isProperty } from '../../utils/characteristicsRowUtils';
import { CharacteristicsRowTypes } from '../../types';
import LinkableTypeReference from '../../sharedUI/components/LinkableTypeReference';
import ParameterTable from '../../sharedUI/components/ParameterTable';
import ReturnsTable from '../../sharedUI/components/ReturnsTable';
import useIteratorStyles from './Iterator.styles';
import { APIReferenceDescriptionProps } from '../../sharedUI/components/APIReferenceDescription';
import { getVisibleReferenceTags } from '../../sharedUI/utils/tagUtils';
import ReferenceMemberTags from '../../sharedUI/components/ReferenceMemberTags';

type IteratorProps = APIReferenceDescriptionProps & {
  name: string;
  type?: string;
  codeSamples: CodeSample[] | null;
  securities: SecurityTag[] | null;
  threadSafety: ThreadSafetyTag | null;
  row: CharacteristicsRowTypes;
  renderAsPage?: boolean;
  tags: EngineTag[] | null | undefined;
  id?: string;
};

const Iterator: FunctionComponent<React.PropsWithChildren<IteratorProps>> = ({
  name,
  type,
  deprecationMessage,
  tags,
  securities,
  threadSafety,
  description,
  row,
  codeSamples,
  renderAsPage,
  id,
}) => {
  const { translate } = useTranslation();
  const {
    classes: { deprecationMessage: deprecationMessageStyle },
  } = useIteratorStyles();
  const allTags = useMemo(() => {
    return getVisibleReferenceTags({
      tags: (tags || []) as ApiTag[],
      security: securities,
      threadSafety,
    });
  }, [tags, securities, threadSafety]);

  return (
    <div>
      <Grid container alignItems='baseline' spacing={1}>
        <Grid item>
          <Typography
            variant={renderAsPage ? 'h1' : 'h3'}
            component={renderAsPage ? 'h1' : 'h3'}
            id={id ?? getMemberSlug(name)}>
            {name}
          </Typography>
        </Grid>
        {typeof type !== 'undefined' ? (
          <Grid item>
            <LinkableTypeReference type={type} />
          </Grid>
        ) : null}
      </Grid>
      <Grid container marginTop={1.5} marginBottom={2}>
        <Grid item>
          <ReferenceMemberTags tags={allTags} memberName={name} />
        </Grid>
      </Grid>
      {deprecationMessage && (
        <AlertMarkdown
          className={deprecationMessageStyle}
          title={translate('Label.Deprecated') || 'Deprecated'}>
          {deprecationMessage}
        </AlertMarkdown>
      )}
      {description !== null ? <StandardMarkdown>{description}</StandardMarkdown> : null}
      {!isProperty(row) ? (
        <Fragment>
          <ParameterTable parameters={row.parameters} />
          <br />
        </Fragment>
      ) : null}
      {(isMethod(row) || isCallback(row)) && <ReturnsTable returnsList={row.returns} />}
      {Array.isArray(codeSamples) && codeSamples.length > 0 && (
        <CodeSamples codeSamples={codeSamples} />
      )}
    </div>
  );
};

export default Iterator;
